import { UndoOutlined } from '@ant-design/icons';
import { InputAdornment, Tooltip } from '@mui/material';
import { Modal, Tabs, Col, Row } from 'antd';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { validateCNPJ, validateCPF } from 'validations-br';
import { pix, payment, availableSoon, receive } from '@portal/config/app.json';

import { PixActions, ReceivableActions } from '@portal/actions';
import { EventType } from '@portal/enum/analytics';
import { TransactionType } from '@portal/models/enumerators';
import { sendEvent } from '@portal/services/firebase';
import { translate } from '@portal/services/i18n';
import {
  maskCpfOrCnpj,
  maskDecimal,
  maskMoney,
  maskPercent,
  unmaskField,
} from '@portal/services/masks';
import { getEnumDescription } from '@portal/utils/enum';
import { getDateDifferenceInDays } from '@portal/utils/helper';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedDateTimePicker from '@portal/components/AdvancedDateTimePicker/AdvancedDateTimePicker';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedSelect from '@portal/components/AdvancedSelect/AdvancedSelect';
import QRCodeDetails from '@portal/components/QRCodeDetails/QRCodeDetails';
import useReduxState from '@portal/hooks/useReduxState';
import * as MessageService from '@portal/services/message';
import { keepOnlyNumbers } from '@portal/utils/format';
import { validateEmail } from '@portal/utils/validate';

interface ManualReceivablaModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  type?: TransactionType | null;
  maxDaysDuePaymentInvoice?: number;
}

type ValidateDueDateResponse = {
  error: boolean;
  message?: 'DATE.INVALID_MIN_DATE' | 'INVALID_BILLET_DUE_DATE';
  interpolation?: {
    interpolationField: string;
    interpolationValue: string;
  };
};
enum ManualCreateReceivableTabs {
  RECEIVABLE_INFO = 'RECEIVABLE_INFO',
  PAYER_INFO = 'PAYER_INFO',
  RECEIVABLE_GENERATED_DATA = 'RECEIVABLE_GENERATED_DATA',
}

const ManualReceivableModal = ({
  setVisible,
  visible,
  type,
  maxDaysDuePaymentInvoice,
}: ManualReceivablaModalProps) => {
  const dispatch = useDispatch();
  const {
    receivable: { manualAddress, receivableLink, manualReceivableResult },
    pix: { pixKeys },
  } = useReduxState();
  const [interestMasked, setInterestMasked] = useState('');
  const [fineMasked, setFineMasked] = useState('');
  const [discountMasked, setDiscountMasked] = useState('');
  const [email, setEmail] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(
    ManualCreateReceivableTabs.RECEIVABLE_INFO
  );
  const [receivable, setReceivable] = useState<models.ManualReceivableFields>({
    type: null,
    name: null,
    taxIdentifier: null,
    amount: null,
    dueDate: null,
    discount: null,
    zipCode: null,
    state: null,
    city: null,
    street: null,
    pixKey: null,
    instructions: null,
    neighborhood: null,
    interest: null,
    fine: null,
  });

  const setInterestPercentage = (newPercentage: string) => {
    if (
      newPercentage.length > 6 ||
      Number(keepOnlyNumbers(newPercentage)) / 100 > 100
    ) {
      return;
    }

    setInterestMasked(maskDecimal(newPercentage));
    onFormChange('interest', Number(keepOnlyNumbers(newPercentage)) / 100);
  };

  const setFineMask = (newFine: string) => {
    if (newFine.length > 6 || Number(keepOnlyNumbers(newFine)) / 100 > 100) {
      return;
    }
    setFineMasked(maskDecimal(newFine));
    onFormChange('fine', Number(keepOnlyNumbers(newFine)) / 100);
  };

  const setDiscountMask = (newDiscount: string) => {
    if (
      newDiscount.length > 6 ||
      Number(keepOnlyNumbers(newDiscount)) / 100 > 100
    ) {
      return;
    }
    setDiscountMasked(maskDecimal(newDiscount));
    onFormChange('discount', Number(keepOnlyNumbers(newDiscount)) / 100);
  };

  const onFormChange = (key: string, value: string | number) =>
    setReceivable((old) => ({
      ...old,
      [key]: value,
    }));

  const isValidDueDateMaxDays = (): boolean => {
    if (maxDaysDuePaymentInvoice && receivable.dueDate) {
      const dateFormat = translate(
        'APPLICATION.DEFAULT_DATE_FORMAT.LUXON_YEAR_MONTH_DAY'
      );
      const dueDateTimeFromFormat = DateTime.fromFormat(
        receivable.dueDate,
        dateFormat
      );
      const todayDateTime = DateTime.fromJSDate(new Date());

      const differenceInDays = getDateDifferenceInDays(
        todayDateTime,
        dueDateTimeFromFormat
      );
      return differenceInDays <= maxDaysDuePaymentInvoice;
    }
    return true;
  };
  const isValidMinDate = (): boolean => {
    if (receivable.dueDate) {
      const dateFormat = translate(
        'APPLICATION.DEFAULT_DATE_FORMAT.LUXON_YEAR_MONTH_DAY'
      );
      const dueDateTimeFromFormat = DateTime.fromFormat(
        receivable.dueDate,
        dateFormat
      );
      const todayDateTime = DateTime.fromJSDate(new Date());

      return (
        dueDateTimeFromFormat.startOf('day') >= todayDateTime.startOf('day')
      );
    }
    return true;
  };

  const validateDueDate = (): ValidateDueDateResponse => {
    if (!isValidDueDateMaxDays()) {
      return {
        error: true,
        message: 'INVALID_BILLET_DUE_DATE',
        interpolation: {
          interpolationField: 'maxDays',
          interpolationValue: maxDaysDuePaymentInvoice?.toString() ?? '',
        },
      };
    }
    if (!isValidMinDate()) {
      return {
        error: true,
        message: 'DATE.INVALID_MIN_DATE',
      };
    }
    return { error: false };
  };

  const finishManualCreateReceivable = () => {
    if (
      receivable.taxIdentifier &&
      !validateCPF(receivable.taxIdentifier) &&
      !validateCNPJ(receivable.taxIdentifier)
    ) {
      return MessageService.error('INVALID_TAX_IDENTIFIER');
    }

    if (receivable.type === TransactionType.PAYMENT_LINK) {
      return dispatch(ReceivableActions.getReceivableLink(receivable));
    }

    if (
      receivable.type === TransactionType.BILLET &&
      email &&
      !validateEmail(email)
    ) {
      return MessageService.error('INVALID_EMAIL');
    }

    dispatch(
      ReceivableActions.createManualReceivable({
        ...receivable,
        pixKey: receivable.pixKey ?? null,
      })
    );
  };

  const getReceivableTypeOptions = () => {
    const options: models.SelectOptions[] = [];
    const targetTypes = [
      payment.manualReceivablePix ? TransactionType.PIX : undefined,
      payment.manualReceivableBillet ? TransactionType.BILLET : undefined,
      payment.manualReceivableBilletPix
        ? TransactionType.BILLET_PIX
        : undefined,
    ];

    targetTypes.forEach(
      (type) =>
        type &&
        options.push({
          name: getEnumDescription(type, 'TRANSACTION_TYPE'),
          value: type,
        })
    );

    return options;
  };

  const getPixKeyOptions = (): models.SelectOptions[] => {
    if (pixKeys) {
      return pixKeys.map(({ key }) => ({
        name: key,
        value: key,
      }));
    }

    return [];
  };

  const validateReceivableInfo = () => {
    if (
      !receivable.amount ||
      (receivable.amount && receivable.amount / 100 < receive.minAmountValue)
    ) {
      return false;
    }

    if (
      receivable.type !== TransactionType.BILLET &&
      receivable.type !== TransactionType.BILLET_PIX &&
      !receivable.dueDate
    ) {
      return false;
    }

    if (receivable.type === TransactionType.BILLET && !receivable.dueDate) {
      return false;
    }

    if (
      (receivable.type === TransactionType.PIX ||
        receivable.type === TransactionType.PAYMENT_LINK ||
        receivable.type === TransactionType.BILLET_PIX) &&
      !receivable.pixKey
    ) {
      return false;
    }

    return true;
  };

  const validadePayerInfo = () => {
    if (!receivable.name) {
      return false;
    }

    if (
      (receivable.type !== TransactionType.BILLET ||
        payment.receivableAddressInBilletCharge) &&
      (!receivable.zipCode ||
        !receivable.city ||
        !receivable.state ||
        !receivable.street ||
        !receivable.neighborhood)
    ) {
      return false;
    }

    if (
      receivable.taxIdentifier?.length !== 11 &&
      receivable.taxIdentifier?.length !== 14
    ) {
      return false;
    }

    if (
      payment.receivableEmailInBilletCharge &&
      receivable.type === TransactionType.BILLET &&
      !email
    ) {
      return false;
    }

    return true;
  };

  const copyToClipboard = (value?: string | null) => {
    if (value) {
      navigator.clipboard.writeText(value);
      MessageService.success(
        'COMPONENTS.MANUAL_MODAL.MESSAGE.COPY_TO_CLIPBOARD'
      );
    }
  };

  const handleSendByEmail = () => {
    const billetUrl = manualReceivableResult?.contentUrl;

    if (billetUrl && email) {
      dispatch(ReceivableActions.postSendReceivableByEmail(billetUrl, email));
    }
  };

  useEffect(() => {
    if (!visible) {
      dispatch(ReceivableActions.cleanReceivableLink());
      dispatch(ReceivableActions.cleanManualReceivableResult());
      setCurrentTab(ManualCreateReceivableTabs.RECEIVABLE_INFO);
      setInterestMasked('');
      setFineMasked('');
      setEmail(null);
      setReceivable({
        amount: null,
        name: null,
        taxIdentifier: null,
        dueDate: null,
        type: null,
        city: null,
        state: null,
        discount: null,
        street: null,
        zipCode: null,
        pixKey: null,
        instructions: null,
        neighborhood: null,
        interest: null,
        fine: null,
      });
    } else {
      setReceivable((prev) => ({ ...prev, type }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (receivable.zipCode?.length === 8) {
      dispatch(ReceivableActions.getAddress(receivable.zipCode));
      dispatch(ReceivableActions.cleanAddress());
    }
  }, [receivable.zipCode, dispatch]);

  useEffect(() => {
    if (manualAddress) {
      setReceivable((old) => ({
        ...old,
        ...manualAddress,
      }));
    }
  }, [manualAddress]);

  const onNextStep = () => {
    const dateValidation = validateDueDate();
    if (dateValidation.error && dateValidation.message) {
      return MessageService.error(dateValidation.message, {
        interpolation: dateValidation.interpolation,
      });
    }

    setCurrentTab(ManualCreateReceivableTabs.PAYER_INFO);
  };
  useEffect(() => {
    if (
      (receivable.type === TransactionType.PAYMENT_LINK && receivableLink) ||
      (receivable.type !== TransactionType.PAYMENT_LINK &&
        manualReceivableResult)
    ) {
      setCurrentTab(ManualCreateReceivableTabs.RECEIVABLE_GENERATED_DATA);
      handleSendByEmail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualReceivableResult, receivableLink]);

  useEffect(() => {
    if (pix.enabled && !pixKeys && !availableSoon.pix) {
      dispatch(PixActions.getAllPixKeys());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      open={visible}
      footer={null}
      onCancel={() => setVisible(false)}
      width={1200}
      zIndex={90}
    >
      <div className="manual-receivable-modal">
        <h1 className="manual-receivable-modal__title">
          {translate(
            type === TransactionType.PAYMENT_LINK
              ? 'COMPONENTS.MANUAL_MODAL.PAYMENT_LINK_TITLE'
              : 'COMPONENTS.MANUAL_MODAL.TITLE'
          )}
        </h1>

        <Tabs
          defaultActiveKey={ManualCreateReceivableTabs.RECEIVABLE_INFO}
          activeKey={currentTab}
        >
          <Tabs.TabPane
            tab={translate('COMPONENTS.MANUAL_MODAL.TABS.RECEIVABLE_INFO')}
            key={ManualCreateReceivableTabs.RECEIVABLE_INFO}
            disabled
          >
            <Row className="manual-receivable-modal__content">
              {!type && (
                <Col className="mt-3" sm={6}>
                  <label className="manual-receivable-modal__content__label">
                    {translate('COMPONENTS.MANUAL_MODAL.FIELDS.TYPE')}
                  </label>
                  <AdvancedSelect
                    variant="standard"
                    options={getReceivableTypeOptions()}
                    value={receivable.type}
                    onChange={(value) =>
                      setReceivable((old) => ({ ...old, type: value }))
                    }
                  />
                </Col>
              )}
              <Col className="mt-3" sm={6}>
                <label className="manual-receivable-modal__content__label">
                  {translate('COMPONENTS.MANUAL_MODAL.FIELDS.AMOUNT')}
                </label>
                <AdvancedInput
                  style={!type ? { marginTop: 16 } : {}}
                  value={maskMoney(
                    receivable.amount ? receivable.amount.toString() : ''
                  )}
                  variant="standard"
                  onChange={(value) => {
                    onFormChange('amount', unmaskField(value));
                  }}
                />
              </Col>

              {receivable?.type === TransactionType.BILLET && (
                <Col sm={6}>
                  <label className="manual-receivable-modal__content__label">
                    {translate('COMPONENTS.MANUAL_MODAL.FIELDS.INTEREST')}
                  </label>
                  <AdvancedInput
                    style={!type ? { marginTop: 16 } : {}}
                    variant="standard"
                    startIcon={
                      <InputAdornment position="start">
                        {translate('COMPONENTS.MANUAL_MODAL.PERCENT_SIGN')}
                      </InputAdornment>
                    }
                    value={interestMasked}
                    onChange={setInterestPercentage}
                  />
                </Col>
              )}
              {receivable.type && receivable.type === TransactionType.BILLET ? (
                <>
                  <Col sm={6}>
                    <label className="manual-receivable-modal__content__label">
                      {translate('COMPONENTS.MANUAL_MODAL.FIELDS.FINE_AMOUNT')}
                    </label>
                    <AdvancedInput
                      startIcon={
                        <InputAdornment position="start">
                          {translate('COMPONENTS.MANUAL_MODAL.PERCENT_SIGN')}
                        </InputAdornment>
                      }
                      style={!type ? { marginTop: 16 } : {}}
                      variant="standard"
                      value={fineMasked}
                      onChange={setFineMask}
                    />
                  </Col>
                </>
              ) : null}
              {receivable.type ? (
                <Col className="mt-3" sm={6}>
                  <label className="manual-receivable-modal__content__label">
                    {translate('COMPONENTS.MANUAL_MODAL.FIELDS.DUE_DATE')}
                  </label>
                  <AdvancedDateTimePicker
                    onlyDate
                    disablePast
                    style={!type ? { marginTop: 16 } : {}}
                    variant="standard"
                    value={receivable.dueDate ?? null}
                    onChange={(value) =>
                      onFormChange('dueDate', value ? value.toISODate() : '')
                    }
                  />
                </Col>
              ) : null}
              {receivable.type &&
              receivable.type === TransactionType.BILLET &&
              payment.enableDiscountInputBillet ? (
                <Col className="mt-3" sm={6}>
                  <label className="manual-receivable-modal__content__label">
                    {translate('COMPONENTS.MANUAL_MODAL.FIELDS.DISCOUNT')}
                  </label>
                  <AdvancedInput
                    startIcon={
                      <InputAdornment position="start">
                        {translate('COMPONENTS.MANUAL_MODAL.PERCENT_SIGN')}
                      </InputAdornment>
                    }
                    style={!type ? { marginTop: 16 } : {}}
                    variant="standard"
                    value={discountMasked}
                    onChange={setDiscountMask}
                  />
                </Col>
              ) : null}
              {receivable.type &&
              receivable.type !== TransactionType.BILLET &&
              pixKeys ? (
                <Col sm={6}>
                  <label className="manual-receivable-modal__content__label">
                    {translate('COMPONENTS.MANUAL_MODAL.FIELDS.PIX_KEY')}
                  </label>
                  <AdvancedSelect
                    variant="standard"
                    options={getPixKeyOptions()}
                    value={receivable.pixKey}
                    onChange={(value) =>
                      setReceivable((old) => ({ ...old, pixKey: value }))
                    }
                  />
                </Col>
              ) : null}

              {receivable.type &&
              receivable.type !== TransactionType.BILLET &&
              !pixKeys ? (
                <Col sm={6}>
                  <label className="manual-receivable-modal__content__label">
                    {translate('COMPONENTS.MANUAL_MODAL.FIELDS.PIX_KEY')}
                    <Tooltip
                      title={translate(
                        'COMPONENTS.MANUAL_MODAL.PIX_ERROR.TOOLTIP'
                      )}
                    >
                      <UndoOutlined
                        className="manual-receivable-modal__content__icon"
                        onClick={() => {
                          if (!pixKeys) {
                            dispatch(PixActions.getAllPixKeys());
                          }
                        }}
                      />
                    </Tooltip>
                  </label>
                  <AdvancedSelect
                    variant="standard"
                    disabled={true}
                    label={
                      <span>
                        {translate('COMPONENTS.MANUAL_MODAL.PIX_ERROR.MESSAGE')}
                      </span>
                    }
                  />
                </Col>
              ) : null}
            </Row>

            <div className="manual-receivable-modal__footer">
              <AdvancedButton
                text={translate('COMPONENTS.MANUAL_MODAL.BUTTONS.NEXT')}
                onClick={onNextStep}
                disabled={!validateReceivableInfo()}
              />
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane
            tab={translate('COMPONENTS.MANUAL_MODAL.TABS.PAYER_INFO')}
            key={ManualCreateReceivableTabs.PAYER_INFO}
            disabled
          >
            <Row className="manual-receivable-modal__content">
              <Col sm={4}>
                <label className="manual-receivable-modal__content__label">
                  {translate('COMPONENTS.MANUAL_MODAL.FIELDS.NAME')}
                </label>
                <AdvancedInput
                  variant="standard"
                  value={receivable.name}
                  onChange={(value) => onFormChange('name', value)}
                />
              </Col>
              <Col sm={4}>
                <label className="manual-receivable-modal__content__label">
                  {translate('COMPONENTS.MANUAL_MODAL.FIELDS.TAX_IDENTIFIER')}
                </label>
                <AdvancedInput
                  variant="standard"
                  value={maskCpfOrCnpj(receivable.taxIdentifier ?? '')}
                  onChange={(value) => {
                    if (unmaskField(value).length <= 14) {
                      onFormChange('taxIdentifier', unmaskField(value));
                    }
                  }}
                />
              </Col>
              {receivable.type === TransactionType.BILLET &&
              payment.receivableEmailInBilletCharge ? (
                <Col sm={4}>
                  <label className="manual-receivable-modal__content__label">
                    {translate('COMPONENTS.MANUAL_MODAL.FIELDS.EMAIL')}
                  </label>
                  <AdvancedInput
                    variant="standard"
                    value={email ?? ''}
                    onChange={(value) => setEmail(value)}
                  />
                </Col>
              ) : null}
            </Row>

            {receivable.type !== TransactionType.BILLET ||
            payment.receivableAddressInBilletCharge ? (
              <>
                <Row className="manual-receivable-modal__content">
                  <Col sm={3}>
                    <label className="manual-receivable-modal__content__label">
                      {translate('COMPONENTS.MANUAL_MODAL.FIELDS.ZIP_CODE')}
                    </label>
                    <AdvancedInput
                      value={receivable.zipCode ?? ''}
                      variant="standard"
                      onChange={(value) => {
                        const cleanAddress = unmaskField(value);
                        if (cleanAddress.length <= 8) {
                          onFormChange('zipCode', cleanAddress);
                        }
                      }}
                    />
                  </Col>
                  <Col sm={3}>
                    <label className="manual-receivable-modal__content__label">
                      {translate('COMPONENTS.MANUAL_MODAL.FIELDS.STATE')}
                    </label>
                    <AdvancedInput
                      value={receivable.state ?? ''}
                      variant="standard"
                      onChange={(value) => onFormChange('state', value)}
                      readOnly
                      disabled
                    />
                  </Col>
                  <Col sm={3}>
                    <label className="manual-receivable-modal__content__label">
                      {translate('COMPONENTS.MANUAL_MODAL.FIELDS.CITY')}
                    </label>
                    <AdvancedInput
                      value={receivable.city ?? ''}
                      variant="standard"
                      onChange={(value) => onFormChange('city', value)}
                      readOnly
                      disabled
                    />
                  </Col>
                </Row>
                <Row className="manual-receivable-modal__content">
                  <Col sm={6}>
                    <label className="manual-receivable-modal__content__label">
                      {translate('COMPONENTS.MANUAL_MODAL.FIELDS.STREET')}
                    </label>
                    <AdvancedInput
                      value={receivable.street ?? ''}
                      variant="standard"
                      onChange={(value) => onFormChange('street', value)}
                    />
                  </Col>
                  <Col sm={6}>
                    <label className="manual-receivable-modal__content__label">
                      {translate('COMPONENTS.MANUAL_MODAL.FIELDS.NEIGHBORHOOD')}
                    </label>
                    <AdvancedInput
                      value={receivable.neighborhood ?? ''}
                      variant="standard"
                      onChange={(value) => onFormChange('neighborhood', value)}
                    />
                  </Col>
                </Row>
              </>
            ) : null}
            {(receivable.type === TransactionType.BILLET ||
              receivable.type === TransactionType.PAYMENT_LINK ||
              receivable.type === TransactionType.BILLET_PIX) && (
              <Row className="manual-receivable-modal__content">
                <Col sm={12}>
                  <label className="manual-receivable-modal__content__label">
                    {translate('COMPONENTS.MANUAL_MODAL.FIELDS.DESCRIPTION')}
                  </label>
                  <AdvancedInput
                    value={receivable.instructions ?? ''}
                    variant="standard"
                    onChange={(value) => onFormChange('instructions', value)}
                  />
                </Col>
              </Row>
            )}
            <div className="manual-receivable-modal__footer">
              <AdvancedButton
                text={translate('COMPONENTS.MANUAL_MODAL.BUTTONS.SUBMIT')}
                onClick={finishManualCreateReceivable}
                disabled={!validadePayerInfo()}
              />
            </div>
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={translate(
              'COMPONENTS.MANUAL_MODAL.TABS.RECEIVABLE_GENERATED_DATA'
            )}
            key={ManualCreateReceivableTabs.RECEIVABLE_GENERATED_DATA}
            disabled
          >
            <div className="manual-receivable-modal__content">
              {(() => {
                switch (receivable.type) {
                  case TransactionType.PAYMENT_LINK:
                    return (
                      <>
                        <Row>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.AMOUNT'
                              )}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {maskMoney(
                                receivable?.amount
                                  ? receivable.amount.toString()
                                  : ''
                              )}
                            </p>
                          </Col>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate('COMPONENTS.MANUAL_MODAL.FIELDS.NAME')}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {receivable.name ?? ''}
                            </p>
                          </Col>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.TAX_IDENTIFIER'
                              )}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {maskCpfOrCnpj(receivable.taxIdentifier ?? '')}
                            </p>
                          </Col>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.DUE_DATE'
                              )}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {DateTime.fromISO(
                                receivable.dueDate ?? ''
                              ).toLocaleString()}
                            </p>
                          </Col>
                          {receivable.instructions ? (
                            <Col className="manual-receivable-modal__content__item">
                              <b className="manual-receivable-modal__content__label">
                                {translate(
                                  'COMPONENTS.MANUAL_MODAL.FIELDS.DESCRIPTION'
                                )}
                              </b>
                              <p className="manual-receivable-modal__content__item__info">
                                {receivable.instructions ?? ''}
                              </p>
                            </Col>
                          ) : null}
                        </Row>
                        <Row className="mt-3">
                          <Col>
                            <div>
                              <b className="manual-receivable-modal__content__label">
                                {translate(
                                  'COMPONENTS.MANUAL_MODAL.FIELDS.LINK'
                                )}
                              </b>
                              <div className="manual-receivable-modal__content__item__info">
                                <div className="manual-receivable-modal__content__item__wrapper">
                                  <AdvancedInput
                                    value={receivableLink ?? ''}
                                    readOnly
                                    onClick={() => {
                                      copyToClipboard(receivableLink);
                                      sendEvent(
                                        EventType.RECEIVABLE_SHARE_PAYMENT_LINK
                                      );
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </>
                    );

                  case TransactionType.BILLET:
                    return (
                      <>
                        <Row>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.AMOUNT'
                              )}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {maskMoney(
                                receivable?.amount
                                  ? receivable.amount.toString()
                                  : ''
                              )}
                            </p>
                          </Col>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate('COMPONENTS.MANUAL_MODAL.FIELDS.NAME')}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {receivable.name ?? ''}
                            </p>
                          </Col>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.TAX_IDENTIFIER'
                              )}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {maskCpfOrCnpj(receivable.taxIdentifier ?? '')}
                            </p>
                          </Col>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.DUE_DATE'
                              )}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {DateTime.fromISO(
                                receivable.dueDate ?? ''
                              ).toLocaleString()}
                            </p>
                          </Col>
                          {receivable.instructions ? (
                            <Col className="manual-receivable-modal__content__item">
                              <b className="manual-receivable-modal__content__label">
                                {translate(
                                  'COMPONENTS.MANUAL_MODAL.FIELDS.DESCRIPTION'
                                )}
                              </b>
                              <p className="manual-receivable-modal__content__item__info">
                                {receivable.instructions ?? ''}
                              </p>
                            </Col>
                          ) : null}
                        </Row>
                        <Row>
                          {receivable.interest ? (
                            <Col md={3}>
                              <b className="manual-receivable-modal__content__label">
                                {translate(
                                  'COMPONENTS.MANUAL_MODAL.FIELDS.INTEREST'
                                )}
                              </b>
                              <p className="manual-receivable-modal__content__item__info">
                                {maskPercent(
                                  receivable.interest
                                    ? `${receivable.interest.toFixed(2)}`
                                    : ''
                                )}
                              </p>
                            </Col>
                          ) : null}
                          {receivable.fine ? (
                            <Col md={3}>
                              <b className="manual-receivable-modal__content__label">
                                {translate(
                                  'COMPONENTS.MANUAL_MODAL.FIELDS.FINE_AMOUNT'
                                )}
                              </b>
                              <p className="manual-receivable-modal__content__item__info">
                                {maskPercent(
                                  receivable.fine
                                    ? `${receivable.fine.toFixed(2)}`
                                    : ''
                                )}
                              </p>
                            </Col>
                          ) : null}
                        </Row>
                        <Row className="mt-3">
                          <Col sm={10}>
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.CHARGE_CODE'
                              )}
                            </b>
                            <div className="manual-receivable-modal__content__item__info">
                              <AdvancedInput
                                value={manualReceivableResult?.id ?? ''}
                                readOnly
                                onClick={() =>
                                  copyToClipboard(manualReceivableResult?.id)
                                }
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm={10}>
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.TYPEABLE_LINE'
                              )}
                            </b>
                            <div className="manual-receivable-modal__content__item__info">
                              <AdvancedInput
                                value={
                                  manualReceivableResult?.billetInfo
                                    ?.typeableLine ?? ''
                                }
                                readOnly
                                onClick={() =>
                                  copyToClipboard(
                                    manualReceivableResult?.billetInfo
                                      ?.typeableLine
                                  )
                                }
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm={10}>
                            <div>
                              <b className="manual-receivable-modal__content__label">
                                {translate(
                                  'COMPONENTS.MANUAL_MODAL.FIELDS.URL'
                                )}
                              </b>
                              <div className="manual-receivable-modal__content__item__info">
                                <div className="manual-receivable-modal__content__item__wrapper">
                                  <AdvancedInput
                                    fullWidth={true}
                                    value={
                                      payment.customBillet
                                        ? manualReceivableResult?.contentUrl
                                        : manualReceivableResult?.billetInfo
                                            ?.url ?? ''
                                    }
                                    readOnly
                                    onClick={() =>
                                      copyToClipboard(
                                        payment.customBillet
                                          ? manualReceivableResult?.contentUrl
                                          : manualReceivableResult?.billetInfo
                                              ?.url
                                      )
                                    }
                                  />
                                  <AdvancedButton
                                    text={translate(
                                      'COMPONENTS.MANUAL_MODAL.BUTTONS.DOWNLOAD'
                                    )}
                                    onClick={() => {
                                      window.location.assign(
                                        payment.customBillet
                                          ? manualReceivableResult?.contentUrl ??
                                              ''
                                          : manualReceivableResult?.billetInfo
                                              ?.url ?? ''
                                      );
                                    }}
                                    disabled={
                                      !manualReceivableResult?.billetInfo?.url
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </>
                    );

                  case TransactionType.PIX:
                    return (
                      <>
                        <Row>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.AMOUNT'
                              )}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {maskMoney(
                                receivable?.amount
                                  ? receivable.amount.toString()
                                  : ''
                              )}
                            </p>
                          </Col>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate('COMPONENTS.MANUAL_MODAL.FIELDS.NAME')}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {receivable.name ?? ''}
                            </p>
                          </Col>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.TAX_IDENTIFIER'
                              )}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {maskCpfOrCnpj(receivable.taxIdentifier ?? '')}
                            </p>
                          </Col>
                          {manualReceivableResult?.pixInfo?.bank ? (
                            <Col className="manual-receivable-modal__content__item">
                              <b className="manual-receivable-modal__content__label">
                                {translate(
                                  'COMPONENTS.MANUAL_MODAL.FIELDS.BANK'
                                )}
                              </b>
                              <p className="manual-receivable-modal__content__item__info">
                                {manualReceivableResult?.pixInfo?.bank ?? ''}
                              </p>
                            </Col>
                          ) : null}
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.DUE_DATE'
                              )}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {DateTime.fromISO(
                                receivable.dueDate ?? ''
                              ).toLocaleString()}
                            </p>
                          </Col>
                          {receivable.instructions ? (
                            <Col className="manual-receivable-modal__content__item">
                              <b className="manual-receivable-modal__content__label">
                                {translate(
                                  'COMPONENTS.MANUAL_MODAL.FIELDS.DESCRIPTION'
                                )}
                              </b>
                              <p className="manual-receivable-modal__content__item__info">
                                {receivable.instructions ?? ''}
                              </p>
                            </Col>
                          ) : null}
                        </Row>
                        <Row className="mt-3">
                          <Col>
                            <Row>
                              <Col sm={12}>
                                <b className="manual-receivable-modal__content__label">
                                  {translate(
                                    'COMPONENTS.MANUAL_MODAL.FIELDS.CHARGE_CODE'
                                  )}
                                </b>
                                <div className="manual-receivable-modal__content__item__info">
                                  <AdvancedInput
                                    value={manualReceivableResult?.id ?? ''}
                                    readOnly
                                    onClick={() =>
                                      copyToClipboard(
                                        manualReceivableResult?.id
                                      )
                                    }
                                  />
                                </div>
                              </Col>
                            </Row>
                            <Row>
                              <Col sm={12}>
                                <b className="manual-receivable-modal__content__label">
                                  {translate(
                                    'COMPONENTS.MANUAL_MODAL.FIELDS.PIX_KEY'
                                  )}
                                </b>
                                <div className="manual-receivable-modal__content__item__info">
                                  <AdvancedInput
                                    value={
                                      manualReceivableResult?.pixInfo?.key ?? ''
                                    }
                                    readOnly
                                    onClick={() =>
                                      copyToClipboard(
                                        manualReceivableResult?.pixInfo?.key ??
                                          ''
                                      )
                                    }
                                  />
                                </div>
                              </Col>
                            </Row>
                          </Col>
                          <Col
                            className="manual-receivable-modal__qrcode_pix mt-3"
                            sm={5}
                          >
                            <QRCodeDetails
                              content={
                                manualReceivableResult?.pixInfo
                                  ?.qrCodeContent ?? ''
                              }
                              hideHeader
                              description={false}
                              buttons={true}
                            />
                          </Col>
                        </Row>
                      </>
                    );

                  case TransactionType.BILLET_PIX:
                    return (
                      <>
                        <Row>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.AMOUNT'
                              )}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {maskMoney(
                                receivable?.amount
                                  ? receivable.amount.toString()
                                  : ''
                              )}
                            </p>
                          </Col>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate('COMPONENTS.MANUAL_MODAL.FIELDS.NAME')}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {receivable.name ?? ''}
                            </p>
                          </Col>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.TAX_IDENTIFIER'
                              )}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {maskCpfOrCnpj(receivable.taxIdentifier ?? '')}
                            </p>
                          </Col>
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.PIX_KEY'
                              )}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {manualReceivableResult?.pixInfo?.key ?? ''}
                            </p>
                          </Col>
                          {manualReceivableResult?.pixInfo?.bank ? (
                            <Col className="manual-receivable-modal__content__item">
                              <b className="manual-receivable-modal__content__label">
                                {translate(
                                  'COMPONENTS.MANUAL_MODAL.FIELDS.BANK'
                                )}
                              </b>
                              <p className="manual-receivable-modal__content__item__info">
                                {manualReceivableResult?.pixInfo?.bank ?? ''}
                              </p>
                            </Col>
                          ) : null}
                          <Col className="manual-receivable-modal__content__item">
                            <b className="manual-receivable-modal__content__label">
                              {translate(
                                'COMPONENTS.MANUAL_MODAL.FIELDS.DUE_DATE'
                              )}
                            </b>
                            <p className="manual-receivable-modal__content__item__info">
                              {DateTime.fromISO(
                                receivable.dueDate ?? ''
                              ).toLocaleString()}
                            </p>
                          </Col>
                          {receivable.instructions ? (
                            <Col className="manual-receivable-modal__content__item">
                              <b className="manual-receivable-modal__content__label">
                                {translate(
                                  'COMPONENTS.MANUAL_MODAL.FIELDS.DESCRIPTION'
                                )}
                              </b>
                              <p className="manual-receivable-modal__content__item__info">
                                {receivable.instructions ?? ''}
                              </p>
                            </Col>
                          ) : null}
                        </Row>
                        <Row className="mt-3">
                          <Col>
                            <Row>
                              <Col sm={12}>
                                <b className="manual-receivable-modal__content__label">
                                  {translate(
                                    'COMPONENTS.MANUAL_MODAL.FIELDS.CHARGE_CODE'
                                  )}
                                </b>
                                <div className="manual-receivable-modal__content__item__info">
                                  <AdvancedInput
                                    value={manualReceivableResult?.id ?? ''}
                                    readOnly
                                    onClick={() =>
                                      copyToClipboard(
                                        manualReceivableResult?.id
                                      )
                                    }
                                  />
                                </div>
                              </Col>
                            </Row>
                            <Row>
                              <Col sm={12}>
                                <b className="manual-receivable-modal__content__label">
                                  {translate(
                                    'COMPONENTS.MANUAL_MODAL.FIELDS.TYPEABLE_LINE'
                                  )}
                                </b>
                                <div className="manual-receivable-modal__content__item__info">
                                  <AdvancedInput
                                    value={
                                      manualReceivableResult?.billetInfo
                                        ?.typeableLine ?? ''
                                    }
                                    readOnly
                                    onClick={() =>
                                      copyToClipboard(
                                        manualReceivableResult?.billetInfo
                                          ?.typeableLine
                                      )
                                    }
                                  />
                                </div>
                              </Col>
                            </Row>
                            <Row>
                              <Col sm={12}>
                                <div>
                                  <b className="manual-receivable-modal__content__label">
                                    {translate(
                                      'COMPONENTS.MANUAL_MODAL.FIELDS.URL'
                                    )}
                                  </b>
                                  <div className="manual-receivable-modal__content__item__info">
                                    <div className="manual-receivable-modal__content__item__wrapper">
                                      <AdvancedInput
                                        fullWidth={true}
                                        value={
                                          manualReceivableResult?.contentUrl ??
                                          ''
                                        }
                                        readOnly
                                        onClick={() =>
                                          copyToClipboard(
                                            manualReceivableResult?.contentUrl
                                          )
                                        }
                                      />
                                      <AdvancedButton
                                        text={translate(
                                          'COMPONENTS.MANUAL_MODAL.BUTTONS.DOWNLOAD'
                                        )}
                                        onClick={() => {
                                          window.location.assign(
                                            manualReceivableResult?.contentUrl ??
                                              ''
                                          );
                                        }}
                                        disabled={
                                          !manualReceivableResult?.contentUrl
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </Col>
                          <Col
                            className="manual-receivable-modal__qrcode_billet"
                            sm={5}
                          >
                            <QRCodeDetails
                              content={
                                manualReceivableResult?.pixInfo
                                  ?.qrCodeContent ?? ''
                              }
                              hideHeader
                              center
                              description={false}
                              buttons={true}
                            />
                          </Col>
                        </Row>
                      </>
                    );

                  default:
                    break;
                }
              })()}

              <div className="manual-receivable-modal__footer">
                <AdvancedButton
                  text={translate('COMPONENTS.MANUAL_MODAL.BUTTONS.CLOSE')}
                  onClick={() => setVisible(false)}
                />
              </div>
            </div>
          </Tabs.TabPane>
        </Tabs>
      </div>
    </Modal>
  );
};

export default ManualReceivableModal;
